import { PlayArrow, Stop } from '@mui/icons-material';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { Focus } from 'lucide-react';
import { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { MovieReviewSlider } from '~/components/Sliders/MovieReviewSlider';
import StructureLabel from '~/components/Structures/StructureLabel';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { useAutoScroll } from '~/hooks/useAutoScroll';
import useViewportFocus from '~/hooks/useViewportFocus';

/**
 * Play/Stop and the scrub slider for one structure under review. Play sweeps
 * the displayed slice through the structure on whichever surface is open.
 * While nothing can be swept (the structure's set is on no view, or the
 * structure has no slices to sweep) Play is disabled with the reason in its
 * tooltip; a running sweep can always be stopped.
 */
export default function ReviewPlayer({
    roi,
    showLabel = true,
}: {
    roi: RoiReadModel;
    showLabel?: boolean;
}) {
    const intl = useIntl();
    const {
        shouldScroll,
        canScroll,
        blockedReasonId,
        startScrolling,
        stopScrolling,
    } = useAutoScroll(roi.roiId);
    const handleAutoScroll = () => {
        if (shouldScroll) {
            stopScrolling();
        } else {
            startScrolling();
        }
    };
    const { focusOnStructure } = useViewportFocus();

    const handleFocusOnStructure = () => {
        focusOnStructure(roi.roiId);
    };

    const playTooltip = buildToolTooltip(
        intl,
        intl.formatMessage({
            id: shouldScroll ? 'review.player.stop' : 'review.player.play',
        }),
        blockedReasonId && !shouldScroll
            ? intl.formatMessage({ id: blockedReasonId })
            : undefined
    );

    return (
        <Fragment>
            {showLabel && (
                <Stack
                    spacing={2}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                >
                    <StructureLabel roi={roi} />

                    <Tooltip
                        title={intl.formatMessage({
                            id: 'review.player.focus',
                        })}
                    >
                        <IconButton
                            aria-label={intl.formatMessage({
                                id: 'focusOnStructure',
                            })}
                            size="small"
                            onClick={handleFocusOnStructure}
                        >
                            <Focus size={16} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )}
            <Stack direction={'row'} alignItems={'center'} gap={1} ml={-0.5}>
                <Tooltip title={playTooltip}>
                    {/* A disabled button fires no pointer events, so the
                        tooltip hangs on the span around it. */}
                    <span>
                        <IconButton
                            aria-label={intl.formatMessage({
                                id: 'autoScrollStructure',
                            })}
                            onClick={handleAutoScroll}
                            disabled={!shouldScroll && !canScroll}
                            size="small"
                            sx={{
                                p: 0,
                                position: 'relative',
                                zIndex: 1,
                            }}
                        >
                            {shouldScroll ? (
                                <Stop fontSize="small" />
                            ) : (
                                <PlayArrow fontSize="small" />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
                <MovieReviewSlider />
            </Stack>
        </Fragment>
    );
}
